'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  AnimatePresence,
  motion,
} from 'framer-motion'

import {
  BellRing,
  TriangleAlert,
} from 'lucide-react'

import {
  WorkspaceAlert,
} from '@/components/alerts/WorkspaceAlert'

import {
  useBusinessMembershipExpiryAlert,
  WorkspaceAlertItem,
} from '@/components/alerts/BusinessMembershipExpiryAlert'

import {
  useEmailQuotaUsageAlert,
} from '@/components/alerts/EmailQuotaUsageAlert'

import {
  cn,
} from '@/lib/utils'

type Props = {
  sellerMembershipActive?: boolean
  sellerMembershipExpiry?: string | null

  paymentUrl?: string | null

  hasStripeSubscription?: boolean
  stripeSubscriptionStatus?: string | null
  stripeCurrentPeriodEnd?: string | null

  className?: string
}

const STORAGE_KEY =
  'hubee:workspace-alert-center:minimized'

const AUTO_REOPEN_MS =
  6 * 60 * 60 * 1000

type MinimizedState = {
  minimizedAt: number
  highestTone: WorkspaceAlertItem['tone']
  alertIds: string[]
}

function getTonePriority(
  tone: WorkspaceAlertItem['tone']
) {
  switch (tone) {
    case 'danger':
      return 4

    case 'warning':
      return 3

    case 'info':
      return 2

    default:
      return 1
  }
}

function getHighestTone(
  alerts: WorkspaceAlertItem[]
): WorkspaceAlertItem['tone'] {
  if (!alerts.length) {
    return 'neutral'
  }

  return alerts.reduce(
    (highest, alert) =>
      getTonePriority(alert.tone) >
      getTonePriority(highest)
        ? alert.tone
        : highest,
    alerts[0].tone
  )
}

function readMinimizedState(): MinimizedState | null {
  if (
    typeof window ===
    'undefined'
  ) {
    return null
  }

  try {
    const raw =
      window.localStorage.getItem(
        STORAGE_KEY
      )

    if (!raw) {
      return null
    }

    const parsed =
      JSON.parse(raw)

    if (
      !parsed ||
      typeof parsed.minimizedAt !== 'number' ||
      !parsed.highestTone
    ) {
      window.localStorage.removeItem(
        STORAGE_KEY
      )

      return null
    }

    return parsed
  } catch {
    return null
  }
}

function saveMinimizedState(
  state: MinimizedState
) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(state)
    )
  } catch {
    // Ignorar si localStorage no está disponible.
  }
}

function clearMinimizedState() {
  try {
    window.localStorage.removeItem(
      STORAGE_KEY
    )
  } catch {
    // noop
  }
}

function getFloatingToneClasses(
  tone: WorkspaceAlertItem['tone']
) {
  switch (tone) {
    case 'danger':
      return {
        icon:
          'text-red-500',
        badge:
          'bg-red-500 text-white',
        glow:
          'bg-red-500/15',
      }

    case 'warning':
      return {
        icon:
          'text-yellow-500',
        badge:
          'bg-yellow-500 text-black',
        glow:
          'bg-yellow-500/15',
      }

    case 'info':
      return {
        icon:
          'text-blue-500',
        badge:
          'bg-blue-500 text-white',
        glow:
          'bg-blue-500/15',
      }

    default:
      return {
        icon:
          'text-foreground',
        badge:
          'bg-foreground text-background',
        glow:
          'bg-primary/10',
      }
  }
}

export function WorkspaceAlertCenter({
  sellerMembershipActive,
  sellerMembershipExpiry,

  paymentUrl,

  hasStripeSubscription,
  stripeSubscriptionStatus,
  stripeCurrentPeriodEnd,

  className,
}: Props) {
  const membershipAlert =
    useBusinessMembershipExpiryAlert({
      sellerMembershipActive,
      sellerMembershipExpiry,

      paymentUrl,

      hasStripeSubscription,
      stripeSubscriptionStatus,
      stripeCurrentPeriodEnd,
    })

  const emailAlert =
    useEmailQuotaUsageAlert()

  const [
    hydrated,
    setHydrated,
  ] = useState(false)

  const [
    activeIndex,
    setActiveIndex,
  ] = useState(0)

  const [
    minimized,
    setMinimized,
  ] = useState(false)

  const alerts =
    useMemo(
      () =>
        [
          membershipAlert,
          emailAlert,
        ].filter(
          (
            item
          ): item is WorkspaceAlertItem =>
            Boolean(item)
        ),
      [
        membershipAlert,
        emailAlert,
      ]
    )

  const highestTone =
    useMemo(
      () =>
        getHighestTone(alerts),
      [alerts]
    )

  /*
   * Hydration + restaurar estado visual.
   */
  useEffect(() => {
    setHydrated(true)

    const saved =
      readMinimizedState()

    if (!saved) {
      return
    }

    const expired =
      Date.now() -
        saved.minimizedAt >=
      AUTO_REOPEN_MS

    if (expired) {
      clearMinimizedState()
      setMinimized(false)
      return
    }

    setMinimized(true)
  }, [])

  /*
   * Si ya no hay alertas reales,
   * limpiamos cualquier estado minimizado.
   */
  useEffect(() => {
    if (!hydrated) return

    if (
      alerts.length === 0
    ) {
      clearMinimizedState()
      setMinimized(false)
      setActiveIndex(0)
    }
  }, [
    alerts.length,
    hydrated,
  ])

  /*
   * Mantener el índice válido.
   */
  useEffect(() => {
    if (
      alerts.length === 0
    ) {
      setActiveIndex(0)
      return
    }

    setActiveIndex(
      (current) =>
        Math.min(
          current,
          alerts.length - 1
        )
    )
  }, [
    alerts.length,
  ])

  /*
   * Si la severidad sube mientras está minimizado,
   * reabrimos automáticamente.
   *
   * Ejemplo:
   * warning -> danger
   */
  useEffect(() => {
    if (
      !hydrated ||
      !minimized ||
      alerts.length === 0
    ) {
      return
    }

    const saved =
      readMinimizedState()

    if (!saved) {
      return
    }

    const severityIncreased =
      getTonePriority(highestTone) >
      getTonePriority(saved.highestTone)

    const currentIds =
      alerts.map((alert) => alert.id)

    const alertsChanged =
      currentIds.some(
        (id) =>
          !saved.alertIds?.includes(id)
      )

    const expired =
      Date.now() -
        saved.minimizedAt >=
      AUTO_REOPEN_MS

    if (
      severityIncreased ||
      alertsChanged ||
      expired
    ) {
      clearMinimizedState()
      setMinimized(false)
    }
  }, [
    highestTone,
    minimized,
    hydrated,
    alerts,
  ])

  const activeAlert =
    alerts[
      activeIndex
    ]

  const goPrevious =
    useCallback(() => {
      if (
        alerts.length <= 1
      ) {
        return
      }

      setActiveIndex(
        (current) =>
          current === 0
            ? alerts.length - 1
            : current - 1
      )
    }, [
      alerts.length,
    ])

  const goNext =
    useCallback(() => {
      if (
        alerts.length <= 1
      ) {
        return
      }

      setActiveIndex(
        (current) =>
          current ===
          alerts.length - 1
            ? 0
            : current + 1
      )
    }, [
      alerts.length,
    ])

  const minimize =
    useCallback(() => {
      saveMinimizedState({
        minimizedAt: Date.now(),
        highestTone,
        alertIds: alerts.map((alert) => alert.id),
      })

      setMinimized(true)
    }, [
      highestTone,
    ])

  const restore =
    useCallback(() => {
      clearMinimizedState()
      setMinimized(false)
    }, [])

  if (
    !hydrated ||
    alerts.length === 0
  ) {
    return null
  }

  const floatingStyles =
    getFloatingToneClasses(
      highestTone
    )

  return (
    <div
      className={cn(
        `
          pointer-events-none
          fixed
          bottom-4
          right-4
          z-[70]

          md:bottom-6
          md:right-6
        `,
        className
      )}
    >
      <AnimatePresence
        mode="wait"
        initial={false}
      >
        {minimized ? (
          <motion.button
            key="minimized"
            type="button"
            onClick={restore}
            initial={{
              opacity: 0,
              scale: 0.9,
              y: 8,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0.94,
              y: 6,
            }}
            transition={{
              duration: 0.2,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
            className="
              pointer-events-auto
              relative
              flex
              h-12 w-12
              items-center justify-center
              overflow-visible
              rounded-2xl
              border border-border/60
              bg-background/92
              shadow-[0_18px_55px_-18px_rgba(0,0,0,0.45)]
              backdrop-blur-2xl
              transition
              hover:scale-[1.04]
              supports-[backdrop-filter]:bg-background/88
            "
            aria-label="Abrir alertas"
          >
            <div
              className={cn(
                `
                  pointer-events-none
                  absolute
                  inset-1
                  rounded-xl
                  blur-xl
                `,
                floatingStyles.glow
              )}
            />

            <div
              className="
                relative
                flex h-9 w-9
                items-center justify-center
                rounded-xl
                bg-muted/50
              "
            >
              {highestTone ===
              'danger' ? (
                <TriangleAlert
                  className={cn(
                    'h-5 w-5',
                    floatingStyles.icon
                  )}
                />
              ) : (
                <BellRing
                  className={cn(
                    'h-5 w-5',
                    floatingStyles.icon
                  )}
                />
              )}
            </div>

            <span
              className={cn(
                `
                  absolute
                  -right-1.5
                  -top-1.5
                  flex
                  h-5
                  min-w-5
                  items-center
                  justify-center
                  rounded-full
                  px-1
                  text-[10px]
                  font-bold
                  tabular-nums
                  shadow-sm
                `,
                floatingStyles.badge
              )}
            >
              {alerts.length}
            </span>
          </motion.button>
        ) : (
          activeAlert && (
            <motion.div
              key={
                activeAlert.id
              }
              initial={{
                opacity: 0,
                y: 14,
                scale: 0.98,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                y: 8,
                scale: 0.985,
              }}
              transition={{
                duration: 0.22,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              }}
              className="
                pointer-events-auto
                w-[calc(100vw-2rem)]
                max-w-[410px]
              "
            >
              <WorkspaceAlert
                tone={
                  activeAlert.tone
                }
                icon={
                  activeAlert.icon
                }
                title={
                  activeAlert.title
                }
                message={
                  activeAlert.message
                }
                meta={
                  activeAlert.meta
                }
                ctaLabel={
                  activeAlert.ctaLabel
                }
                onCta={
                  activeAlert.onCta
                }

                /*
                 * Ahora X significa minimizar.
                 */
                onDismiss={
                  minimize
                }

                currentIndex={
                  activeIndex
                }

                totalAlerts={
                  alerts.length
                }

                onPrevious={
                  goPrevious
                }

                onNext={
                  goNext
                }
              />
            </motion.div>
          )
        )}
      </AnimatePresence>
    </div>
  )
}